import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { getCurrencySymbol } from '../../lib/currency';

const fmtA = (v) => (v == null ? '—' : `A$${Math.round(v).toLocaleString()}`);
const money = (v, sym) => (v == null ? '—' : `${sym}${Math.round(v).toLocaleString()}`);
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const mcls = (v) => (v == null ? 'text-slate-400' : v < 0 ? 'text-rose-600' : v < 30 ? 'text-amber-600' : 'text-emerald-700');

function Stat({ label, value, sub, testId, cls = 'text-[#0F2040]' }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}

/** Per-year schedule, TCO, invoicing and margin (product + blended with implementation). */
export function ProductPricingResults({ preview, blended, implementation, loading }) {
  const [year, setYear] = useState(1);
  if (!preview) return <p className="text-xs text-slate-400" data-testid="pp-results-empty">Enter active users to price the subscription.</p>;
  const t = preview.totals;
  const y = preview.years.find((yy) => yy.year === year) || preview.years[0];
  const ccy = preview.currency || 'AUD';
  const sym = getCurrencySymbol(ccy);
  const fm = (v) => money(v, sym);
  const implLocal = implementation?.sell_aud ? implementation.sell_aud / (preview.fx_to_aud || 1) : 0;
  return (
    <div className={`space-y-4 ${loading ? 'opacity-60' : ''}`} data-testid="product-pricing-results">
      {preview.warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`pp-warning-${i}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}
        </div>
      ))}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <Stat label={`Product TCO · ${preview.term_years} yr · ${ccy}`} value={fm(t.net)} sub={`list ${fm(t.list)} · discounts −${fm(t.term_discount + t.discretionary)}${ccy !== 'AUD' ? ` · ${fmtA(t.net_aud)}` : ''}`} testId="pp-stat-tco" />
        <Stat label="Year 1 subscription" value={fm(t.year1_net)} sub={`avg ${fm(t.avg_annual_net)}/yr · uplift ${preview.escalation_pct}%${preview.term_discount_overridden ? (preview.term_discount_pct ? ` · term disc. ${preview.term_discount_pct}% (book ${preview.book_term_discount_pct}%)` : ' · term discount waived') : ''}`} testId="pp-stat-y1" />
        {preview.support && <Stat label={`Support services · ${preview.support.total_hours}h`} value={fm(t.services_net)} sub={`${preview.support.label}${preview.support.repeat_annually ? ` × ${preview.support.occurrences} yrs` : ''}${preview.support.total_discount > 0 ? ` · list ${fm(t.services_list)} − ${fm(t.services_discount)} (${preview.support.discount_pct}%)` : ''} · margin ${pct(preview.support.margin_pct)} · not ARR · total incl. services ${fm(t.grand_net)}`} testId="pp-stat-support" />}
        <Stat label="Product margin" value={pct(t.margin_pct)} sub={t.cost_known ? `${fm(t.margin)} on ${fm(t.cost)} cost` : 'some costs unknown'} cls={mcls(t.margin_pct)} testId="pp-stat-margin" />
        {blended && (
          <Stat label={`Blended · impl + product${preview.support ? ' + support' : ''}`} value={pct(blended.margin_pct)} cls={mcls(blended.margin_pct)} testId="pp-stat-blended"
            sub={`TCO ${fmtA(blended.tco_aud)} · impl ${fmtA(blended.implementation_sell_aud)}${implementation?.margin_pct != null ? ` @ ${pct(implementation.margin_pct)}` : ''}`} />
        )}
      </div>

      <div>
        <div className="flex items-center gap-1 mb-2" data-testid="pp-year-tabs">
          {preview.years.map((yy) => (
            <button key={yy.year} type="button" onClick={() => setYear(yy.year)} data-testid={`pp-year-tab-${yy.year}`}
              className={`text-[11px] px-2.5 py-1 rounded-md border ${year === yy.year ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              Y{yy.year} <span className="tabular-nums opacity-80">{fm(yy.net)}</span>
            </button>
          ))}
          <span className="ml-auto text-[10px] text-slate-400">{preview.package.label} · {preview.package.au_min.toLocaleString()}–{preview.package.au_max.toLocaleString()} AU · {preview.book?.name}{ccy !== 'AUD' ? ` · 1 ${ccy} = A$${preview.fx_to_aud}` : ''}</span>
        </div>
        <table className="w-full text-xs" data-testid="pp-year-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 text-left">
              <th className="py-1 font-medium">Line</th><th className="py-1 font-medium text-right">Qty</th><th className="py-1 font-medium text-right">Rate</th>
              <th className="py-1 font-medium text-right">List</th><th className="py-1 font-medium text-right">Term disc.</th><th className="py-1 font-medium text-right">Discretionary</th>
              <th className="py-1 font-medium text-right">Net</th><th className="py-1 font-medium text-right">Cost</th><th className="py-1 font-medium text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            {y.lines.map((l) => (
              <tr key={l.key} className="border-t border-slate-100" data-testid={`pp-line-${l.key}`}>
                <td className="py-1.5">
                  <span className="font-medium text-[#0F2040]">{l.label}</span>
                  <span className="ml-1.5 text-[10px] text-slate-400">{l.charged === 'advance' ? 'annual · in advance' : 'monthly · in arrears'}{l.true_up ? ' · true-up' : ''}{l.custom_price ? ' · custom' : ''}</span>
                  {l.discount_reason && <div className="text-[10px] text-amber-700">Discount: {l.discount_reason}</div>}
                </td>
                <td className="py-1.5 text-right tabular-nums">{l.qty.toLocaleString()}</td>
                <td className="py-1.5 text-right tabular-nums">{sym}{Number(l.unit_rate).toLocaleString(undefined, { maximumFractionDigits: 2 })}{l.periods === 12 ? '/mo' : ''}</td>
                <td className="py-1.5 text-right tabular-nums">{fm(l.list)}</td>
                <td className="py-1.5 text-right tabular-nums text-slate-500">{l.term_discount ? `−${fm(l.term_discount)} (${l.term_discount_pct}%)` : '—'}</td>
                <td className="py-1.5 text-right tabular-nums text-amber-700">{l.discretionary ? `−${fm(l.discretionary)} (${l.discretionary_pct}%)` : '—'}</td>
                <td className="py-1.5 text-right tabular-nums font-semibold">{fm(l.net)}</td>
                <td className="py-1.5 text-right tabular-nums text-slate-500">{l.cost == null ? 'n/a' : fm(l.cost)}</td>
                <td className={`py-1.5 text-right tabular-nums font-semibold ${mcls(l.margin_pct)}`}>{pct(l.margin_pct)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 font-semibold" data-testid="pp-year-total">
              <td className="py-1.5">Year {y.year} total</td><td /><td />
              <td className="py-1.5 text-right tabular-nums">{fm(y.list)}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-500">−{fm(y.term_discount)}</td>
              <td className="py-1.5 text-right tabular-nums text-amber-700">−{fm(y.discretionary)}</td>
              <td className="py-1.5 text-right tabular-nums">{fm(y.net)}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-500">{fm(y.cost)}</td>
              <td className={`py-1.5 text-right tabular-nums ${mcls(y.margin_pct)}`}>{pct(y.margin_pct)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-[#0F2040] mb-1">Invoicing schedule <span className="text-slate-400 font-normal">({ccy})</span></h4>
        <table className="w-full text-xs" data-testid="pp-invoicing-table">
          <tbody>
            {implementation?.sell_aud > 0 && (
              <tr className="border-t border-slate-100" data-testid="pp-invoice-implementation">
                <td className="py-1 text-slate-500 w-28 tabular-nums">{preview.start_date || 'on signing'}</td>
                <td className="py-1">Implementation services (one-off, per SOW milestones)</td>
                <td className="py-1 text-right tabular-nums font-medium">{fm(implLocal)}</td>
              </tr>
            )}
            {preview.invoicing.map((inv, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`pp-invoice-${inv.year}-${inv.kind}`}>
                <td className="py-1 text-slate-500 w-28 tabular-nums">{inv.date || (inv.kind === 'advance' ? `Y${inv.year} start` : `Y${inv.year} monthly`)}</td>
                <td className="py-1">{inv.description}</td>
                <td className="py-1 text-right tabular-nums font-medium">{fm(inv.amount)}{inv.kind === 'monthly' ? <span className="text-slate-400 font-normal"> /mo · {fm(inv.annual_amount)}/yr</span> : ''}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 font-semibold" data-testid="pp-invoice-total">
              <td className="py-1.5" colSpan={2}>Total contract value{implementation?.sell_aud > 0 ? ' (implementation + product)' : ''}</td>
              <td className="py-1.5 text-right tabular-nums">{fm(t.net + implLocal)}{ccy !== 'AUD' && blended ? <span className="text-slate-400 font-normal"> · {fmtA(blended.tco_aud)}</span> : ''}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
